import React, { useMemo } from 'react';
import useDemoRestrictions from '../hooks/useDemoRestrictions';
import useDemoConfigQuery from '../persistence/queries/useDemoConfigQuery';
import IDemoConfig from '../types/IDemoConfig';
import { useAuth } from './AuthContext';

interface IDemoConfigContext {
  demoConfig: IDemoConfig | undefined;
  loading: boolean;
  refetchDemoConfig: () => void;
  isDemoMode: boolean;
  canUpload: boolean;
  canUseDropbox: boolean;
  canCreateAnalysis: boolean;
  canGenerateReport: boolean;
  analysisCooldownUntil: string | null;
  reportCooldownUntil: string | null;
  analysisCooldownMessage: string | null;
  reportCooldownMessage: string | null;
  uploadBlockedMessage: string | null;
  dropboxBlockedMessage: string | null;
  analysisBlockedMessage: string | null;
  reportBlockedMessage: string | null;
}

const defaultContext: IDemoConfigContext = {
  demoConfig: undefined,
  loading: false,
  refetchDemoConfig: () => { },
  isDemoMode: false,
  canUpload: true,
  canUseDropbox: true,
  canCreateAnalysis: true,
  canGenerateReport: true,
  analysisCooldownUntil: null,
  reportCooldownUntil: null,
  analysisCooldownMessage: null,
  reportCooldownMessage: null,
  uploadBlockedMessage: null,
  dropboxBlockedMessage: null,
  analysisBlockedMessage: null,
  reportBlockedMessage: null,
};

export const DemoConfigContext = React.createContext<IDemoConfigContext>(defaultContext);

export const DemoConfigProvider: React.FC = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { data, loading, refetch } = useDemoConfigQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const restrictions = useDemoRestrictions(data?.demoConfig);

  const value = useMemo(
    () => ({
      demoConfig: data?.demoConfig,
      loading,
      refetchDemoConfig: () => {
        refetch();
      },
      ...restrictions,
    }),
    [data?.demoConfig, loading, refetch, restrictions],
  );

  return <DemoConfigContext.Provider value={value}>{children}</DemoConfigContext.Provider>;
};

export const useDemoConfig = () => React.useContext(DemoConfigContext);
